import Link from 'next/link';
import React from 'react';
import { IconArrowUpRight } from '@tabler/icons-react';

import { StrapiImage } from '@/components/ui/strapi-image';
import { jakarta } from '@/lib/fonts';
import { cn, formatNumber, truncate } from '@/lib/utils';
import { Product } from '@/types/types';

export const ProductItems = ({
  heading,
  sub_heading,
  products,
  locale,
  basePath = 'products',
  showCursor,
}: {
  heading?: string;
  sub_heading?: string;
  products: Product[];
  locale: string;
  basePath?: string;
  showCursor?: boolean;
}) => {
  const sortedProducts = React.useMemo(() => {
    return products
      ? [...products].sort((a, b) => {
          const orderA = a.order ?? 0;
          const orderB = b.order ?? 0;
          if (orderA !== orderB) return orderA - orderB;
          return (Number(a.id) || 0) - (Number(b.id) || 0);
        })
      : [];
  }, [products]);

  return (
    <div className="py-12 md:py-16">
      {heading && (
        <h2
          className={cn(
            jakarta.className,
            'text-3xl md:text-5xl font-bold text-[#2e1452] dark:text-white mb-4 tracking-tight'
          )}
        >
          {heading}
        </h2>
      )}
      {sub_heading && (
        <p className="text-[#3b2d56] dark:text-neutral-300 text-lg md:text-xl font-medium mb-12 max-w-2xl leading-relaxed">
          {sub_heading}
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
        {sortedProducts.map((product) => (
          <ProductItem
            key={'regular-product-item' + product.id}
            product={product}
            locale={locale}
            basePath={basePath}
            showCursor={showCursor}
          />
        ))}
      </div>
    </div>
  );
};

export const tagColorMap: Record<string, string> = {
  blue: 'bg-indigo-50 text-indigo-700 border-[#e5e2f5] dark:bg-indigo-950/30 dark:text-indigo-400 dark:border-indigo-900/50',
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/50',
  red: 'bg-rose-50 text-[#c02029] border-rose-200 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-900/50',
  purple: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-400 dark:border-purple-900/50',
  yellow: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/50',
  orange: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-900/50',
  pink: 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/30 dark:text-pink-400 dark:border-pink-900/50',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-400 dark:border-indigo-900/50',
  gray: 'bg-neutral-50 text-neutral-700 border-neutral-200 dark:bg-neutral-800/30 dark:text-neutral-400 dark:border-neutral-700/50',
};

const ProductItem = ({
  product,
  locale,
  basePath,
  showCursor,
}: {
  product: Product;
  locale: string;
  basePath: string;
  showCursor?: boolean;
}) => {
  const isTrainingOrService =
    basePath === 'training' ||
    basePath === 'trainings' ||
    basePath === 'services' ||
    basePath === 'service';
  const shouldShowCursor = showCursor ?? isTrainingOrService;

  return (
    <Link
      href={`/${locale}/${basePath}/${product.slug}` as never}
      className="group relative flex flex-col bg-white dark:bg-neutral-900 rounded-3xl overflow-hidden border border-[#e5e2f5] dark:border-neutral-800 hover:border-[#c02029]/40 shadow-xl shadow-[#301754]/5 hover:shadow-2xl hover:shadow-[#c02029]/10 transition-all duration-500 hover:-translate-y-1.5"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-neutral-50 dark:bg-neutral-950 p-4 sm:p-6 flex items-center justify-center border-b border-[#e5e2f5] dark:border-neutral-800">
        {product?.images?.[0]?.url ? (
          <StrapiImage
            src={product.images[0].url}
            alt={product.name}
            width={700}
            height={440}
            className="h-full w-full object-contain rounded-xl transition-transform duration-700 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
            <div className="w-14 h-14 rounded-2xl bg-rose-50 dark:bg-[#c02029]/15 border border-[#c02029]/30 flex items-center justify-center text-[#c02029] text-xl font-black">
              {product.name.charAt(0)}
            </div>
            <span className="text-xs font-semibold text-[#50456c] dark:text-neutral-400 max-w-[200px] truncate">
              {product.name}
            </span>
          </div>
        )}
      </div>

      <div className="p-6 md:p-8 flex flex-col flex-1">
        {product.tags && product.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {product.tags.map((tag) => (
              <span
                key={tag.id}
                className={cn(
                  'px-3 py-1 text-xs font-semibold rounded-full border transition-colors duration-200',
                  tagColorMap[tag.color] || tagColorMap.blue
                )}
              >
                {tag.text}
              </span>
            ))}
          </div>
        )}
        <div className="flex justify-between items-start gap-4 mb-4">
          <h3
            className={cn(
              jakarta.className,
              'text-xl font-bold text-[#2e1452] dark:text-white leading-tight line-clamp-2 group-hover:text-[#c02029] transition-colors'
            )}
          >
            {product.name}
          </h3>
          <div className="flex items-center gap-2.5 shrink-0 mt-0.5">
            {product.show_price !== false && (
              <span className="shrink-0 bg-white dark:bg-neutral-800 border border-[#e5e2f5] dark:border-neutral-700 text-[#2e1452] dark:text-white shadow-sm text-sm font-bold px-3 py-1.5 rounded-full">
                {formatNumber(product.price)} €
              </span>
            )}
            {shouldShowCursor && (
              <div
                aria-label={
                  basePath.startsWith('train')
                    ? (locale === 'fr' ? 'Voir le détail de la formation' : 'View training details')
                    : (locale === 'fr' ? 'Voir le détail du service' : 'View service details')
                }
                className="w-10 h-10 rounded-full bg-[#c02029] text-white flex items-center justify-center shadow-md shadow-[#c02029]/25 group-hover:bg-[#a61b23] group-hover:scale-105 group-hover:shadow-lg group-hover:shadow-[#c02029]/35 transition-all duration-300 shrink-0"
              >
                <IconArrowUpRight className="w-5 h-5 stroke-[2.5] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            )}
          </div>
        </div>
        <p className="text-[#5e5873] dark:text-neutral-300 text-base leading-relaxed mt-auto">
          {truncate(product.description, 130)}
        </p>
      </div>
    </Link>
  );
};

export default ProductItems;
